import {useMemo, useRef, useState, useEffect} from 'react'
import {useVickyAIRecommendations} from '../../../hooks/use-vicky-pdp-recommendations'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {findVickyCarouselBlocks, injectVickyDataIntoContent} from '../../../utils/vicky-cms-utils'

/**
 * Fetches VickyAI data based on cart items and injects it into Optimizely CMS content
 * Only refetches when cart product IDs actually change
 */
export const useCartVickyInjection = (optimizelyData) => {
    const {data: basket} = useCurrentBasket()

    // Get all product IDs from cart for VickyAI (comma-separated string)
    // Use JSON.stringify to create a stable dependency that only changes when actual content changes
    const basketItemsJson = useMemo(() => {
        if (!basket?.productItems?.length) return '[]'
        return JSON.stringify(basket.productItems.map(item => item.productId))
    }, [basket?.productItems])

    const cartSkus = useMemo(() => {
        if (basketItemsJson === '[]') return null

        const productIds = JSON.parse(basketItemsJson)
        const skus = productIds.filter(Boolean).join(',')
        return skus || null
    }, [basketItemsJson])

    // Fetch VickyAI data using all cart items
    const {data: vickyAIData, isLoading: isVickyLoading, products} = useVickyAIRecommendations(
        cartSkus ? 'bought-together' : 'skip',
        cartSkus,
        'CART'
    )


    // Inject VickyAI data into CMS content
    // Create stable key from product IDs to prevent unnecessary recalculation
    const productsKey = useMemo(() => {
        if (!products || products.length === 0) return 'empty'
        return products.map(p => p.id).join(',')
    }, [products])

    const processedOptimizelyContent = useMemo(() => {
        if (!optimizelyData) return null

        const modifiedOptimizelyData = JSON.parse(JSON.stringify(optimizelyData))
        const vickyBlocks = findVickyCarouselBlocks(modifiedOptimizelyData)

        if (vickyBlocks.length > 0 && productsKey !== 'empty') {
            // Create the data structure expected by injectVickyDataIntoContent
            // The utility expects: {boughtTogether: {products, isLoading}}
            const dataToInject = {
                boughtTogether: {
                    products,
                    isLoading: isVickyLoading
                }
            }

            return injectVickyDataIntoContent(modifiedOptimizelyData, dataToInject)
        }

        return modifiedOptimizelyData
    }, [optimizelyData, productsKey, products, isVickyLoading])

    return processedOptimizelyContent
}
